// TelcoTrack — Akvizice KN: typy smluv (katalog + dočasná nabídka v UI/regroup).

import { isPolygonNga } from './polygon-business.constants';

/** Plný katalog typů smluv (validace DB / API / šablony). */
export const ACQUISITION_CONTRACT_TYPES = [
  'souhlas',
  'vstup_na_pozemek',
  'predavaci_protokol',
  'digitalni_cesta',
] as const;

export type AcquisitionContractType = (typeof ACQUISITION_CONTRACT_TYPES)[number];

/**
 * Typy nabízené v UI Smlouvy (akvizice) a vytvářené při KN regroupu / generate.
 * Dočasně jen souhlas — `digitalni_cesta` je vázaná na milník Zřízené VR (viz
 * `ACQUISITION_CONTRACT_TYPES_WORKFLOW`), ne na regroup dle vlastníků.
 * Existující smlouvy ostatních typů v DB zůstávají obsluhovatelné.
 */
export const ACQUISITION_CONTRACT_TYPES_OFFERED = [
  'souhlas',
] as const satisfies ReadonlyArray<AcquisitionContractType>;

export type AcquisitionContractTypeOffered =
  (typeof ACQUISITION_CONTRACT_TYPES_OFFERED)[number];

/**
 * Typy smluv vázané na stavební workflow (ne KN regroup).
 * `digitalni_cesta` = 1 protokol na objekt BUDOVA (ne na každou cestu);
 * spec `digitalniCesta` count = počet cest k instalaci (billing/work), ne počet dokumentů.
 */
export const ACQUISITION_CONTRACT_TYPES_WORKFLOW = [
  'digitalni_cesta',
] as const satisfies ReadonlyArray<AcquisitionContractType>;

export type AcquisitionContractTypeWorkflow =
  (typeof ACQUISITION_CONTRACT_TYPES_WORKFLOW)[number];

/** Typy povolené v `POST …/contracts/generate` (OFFERED ∪ WORKFLOW). */
export const ACQUISITION_CONTRACT_TYPES_GENERATABLE = [
  ...ACQUISITION_CONTRACT_TYPES_OFFERED,
  ...ACQUISITION_CONTRACT_TYPES_WORKFLOW,
] as const satisfies ReadonlyArray<AcquisitionContractType>;

export type AcquisitionContractTypeGeneratable =
  (typeof ACQUISITION_CONTRACT_TYPES_GENERATABLE)[number];

export const ACQUISITION_CONTRACT_TYPE_LABELS: Record<AcquisitionContractType, string> = {
  souhlas: 'Souhlas',
  vstup_na_pozemek: 'Vstup na pozemek',
  predavaci_protokol: 'Předávací protokol',
  digitalni_cesta: 'Digitální cesta',
};

/** Prefix `owners_signature` pro instance protokolu digitální cesty. */
export const DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX = '__digitalni_cesta__:';

/**
 * Prefix `owners_signature` pro souhlas scoped na jednu BUDOVA.
 * Formát: `__souhlas__:{objectId}|{ownersSig}` — unikátní v rámci polygonu
 * (UNIQUE polygon_id + contract_type + owners_signature).
 */
export const SOUHLAS_OWNERS_SIGNATURE_PREFIX = '__souhlas__:';

export function isAcquisitionContractTypeOffered(
  type: string | null | undefined,
): type is AcquisitionContractTypeOffered {
  return (ACQUISITION_CONTRACT_TYPES_OFFERED as readonly string[]).includes(
    (type ?? '').trim().toLowerCase(),
  );
}

export function isDigitalniCestaContractType(type: string | null | undefined): boolean {
  return (type ?? '').trim().toLowerCase() === 'digitalni_cesta';
}

/**
 * Souhlas (akvizice) a digitální cesta: smlouva je vždy na **jednu parcelu budovy**.
 * UI nesmí nabízet Přidat/Odebrat parcelu; PATCH parties nesmí změnit počet parcel.
 * Prázdný / neznámý typ = fallback na schema souhlasu → stejný lock.
 */
export function isAcquisitionSingleBuildingParcelContract(
  type: string | null | undefined,
): boolean {
  const t = (type ?? '').trim().toLowerCase();
  if (!t || t === 'souhlas' || t === 'digitalni_cesta') return true;
  return false;
}

/**
 * Povinná fotodokumentace (sloty 1–3) jen u akvizičního **souhlasu** na polygonu **NGA**.
 * Non-NGA (FTTH, …) a digitální cesta: přílohy nejsou podmínkou generate/sign.
 */
export function acquisitionContractRequiresPhotoAttachments(
  contractType: string | null | undefined,
  constructionType: string | null | undefined,
): boolean {
  if ((contractType ?? '').trim().toLowerCase() !== 'souhlas') return false;
  return isPolygonNga(constructionType);
}

/** Env klíč: vypínač protokolů Digitální cesta (generate/sign + UI nabídka). */
export const ACQ_DIGITALNI_CESTA_ENABLED_ENV = 'ACQ_DIGITALNI_CESTA_ENABLED';

/**
 * Feature flag `ACQ_DIGITALNI_CESTA_ENABLED`.
 * Default zapnuto (unset / prázdné / `1`); vypnutí jen `0` (stejný vzor jako
 * `ACQ_CONTRACTS_GOOGLE_ENABLED`).
 */
export function isAcqDigitalniCestaEnabled(
  envValue: string | null | undefined = undefined,
): boolean {
  let raw = (envValue ?? '').trim();
  if (
    (raw.startsWith('"') && raw.endsWith('"')) ||
    (raw.startsWith("'") && raw.endsWith("'"))
  ) {
    raw = raw.slice(1, -1).trim();
  }
  return raw !== '0';
}

export function isAcquisitionContractTypeGeneratable(
  type: string | null | undefined,
): type is AcquisitionContractTypeGeneratable {
  return (ACQUISITION_CONTRACT_TYPES_GENERATABLE as readonly string[]).includes(
    (type ?? '').trim().toLowerCase(),
  );
}

/**
 * `owners_signature` pro souhlas jedné budovy (jen parcely pod tou BUDOVA).
 * `ownersSig` = klasický podpis vlastnické struktury (`knInternalId-podil|…`).
 */
export function souhlasOwnersSignature(objectId: string, ownersSig: string): string {
  const oid = (objectId ?? '').trim();
  if (!oid) {
    throw new Error('souhlasOwnersSignature: objectId is required');
  }
  const sig = (ownersSig ?? '').trim();
  if (!sig) {
    throw new Error('souhlasOwnersSignature: ownersSig is required');
  }
  return `${SOUHLAS_OWNERS_SIGNATURE_PREFIX}${oid}|${sig}`;
}

/** Parsuje `objectId` z building-scoped souhlas `owners_signature`. */
export function parseSouhlasObjectId(
  ownersSignature: string | null | undefined,
): string | null {
  const raw = (ownersSignature ?? '').trim();
  if (!raw.startsWith(SOUHLAS_OWNERS_SIGNATURE_PREFIX)) return null;
  const rest = raw.slice(SOUHLAS_OWNERS_SIGNATURE_PREFIX.length);
  const pipe = rest.indexOf('|');
  if (pipe <= 0) return null;
  const oid = rest.slice(0, pipe).trim();
  return oid || null;
}

/** True = souhlas vázaný na konkrétní BUDOVA (`__souhlas__:{objectId}|…`). */
export function isSouhlasBuildingScopedSignature(
  ownersSignature: string | null | undefined,
): boolean {
  return parseSouhlasObjectId(ownersSignature) != null;
}

/**
 * `owners_signature` pro protokol digitální cesty dané budovy.
 * Formát: `__digitalni_cesta__:{objectId}:{N}` — unikátní v rámci polygonu
 * (UNIQUE polygon_id + contract_type + owners_signature).
 *
 * Product: vždy jeden dokument na objekt → nové zápisy používají `pathIndex = 1`.
 * `pathIndex` > 1 je legacy (starší multi-path řádky v DB / parsování); display-only.
 */
export function digitalniCestaOwnersSignature(objectId: string, pathIndex: number): string {
  const oid = (objectId ?? '').trim();
  if (!oid) {
    throw new Error('digitalniCestaOwnersSignature: objectId is required');
  }
  const n = Math.trunc(pathIndex);
  if (!Number.isFinite(n) || n < 1) {
    throw new Error(`digitalniCestaOwnersSignature: pathIndex must be >= 1 (got ${pathIndex})`);
  }
  return `${DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX}${oid}:${n}`;
}

/** Parsuje `objectId` z `owners_signature` (`__digitalni_cesta__:{objectId}:{N}`). */
export function parseDigitalniCestaObjectId(
  ownersSignature: string | null | undefined,
): string | null {
  const raw = (ownersSignature ?? '').trim();
  if (!raw.startsWith(DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX)) return null;
  const rest = raw.slice(DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX.length);
  const colon = rest.lastIndexOf(':');
  if (colon <= 0) return null;
  const oid = rest.slice(0, colon).trim();
  return oid || null;
}

/**
 * Parsuje index z `owners_signature`; jinak `null`.
 * Legacy display/compat — produktově je dokument vždy jeden (index 1).
 */
export function parseDigitalniCestaPathIndex(
  ownersSignature: string | null | undefined,
  objectId?: string | null,
): number | null {
  const raw = (ownersSignature ?? '').trim();
  if (!raw.startsWith(DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX)) return null;
  const rest = raw.slice(DIGITALNI_CESTA_OWNERS_SIGNATURE_PREFIX.length);
  // Preferovaný formát: `{objectId}:{N}`
  const colon = rest.lastIndexOf(':');
  if (colon > 0) {
    const oid = rest.slice(0, colon);
    const n = Number.parseInt(rest.slice(colon + 1), 10);
    if (objectId && oid !== objectId.trim()) return null;
    return Number.isFinite(n) && n >= 1 ? n : null;
  }
  // Legacy: `__digitalni_cesta__:N`
  const n = Number.parseInt(rest, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

export type DigitalniCestaMilestoneGate =
  | { ok: true }
  | { ok: false; required: number; signed: number; message: string };

/**
 * Kolik draft protokolů `digitalni_cesta` zajistit při prepare/generate.
 * Vždy přesně 1 dokument na objekt BUDOVA (`pathCount` se ignoruje — spec count
 * = počet cest k instalaci, ne počet smluv). Zachováno kvůli call sites.
 */
export function digitalniCestaContractsEnsureCount(
  _pathCount?: number | null,
): number {
  return 1;
}

/**
 * Gate: milník Zřízené VR × protokol digitální cesty.
 *
 * Protokol je **nepovinný** — nikdy neblokuje dokončení Zřízených VR.
 * Parametry zůstávají kvůli call-site / KMP paritě (signed count, MSO flag).
 */
export function canCompleteZrizeneVrWithDigitalniCesta(_opts: {
  digitalniCestaCount: number | null | undefined;
  signedDigitalniCestaContracts: number;
  /** Legacy: dříve MSO vyžadovalo protokol i při count 0 — už neblokuje. */
  requireEvenWhenCountZero?: boolean;
}): DigitalniCestaMilestoneGate {
  return { ok: true };
}

/** Vstup pro návrh jména podepisující osoby (vlastník / zástupce). */
export interface ContractSignerOwnerRef {
  jmenoNazev?: string | null;
  zastupciKPodpisu?: string | null;
  /** KN typ subjektu — `sjm` spouští rozpad „A a B“ na 2 podpisové sloty. */
  subjektTyp?: string | null;
}

export interface ContractSignerSuggestion {
  /** Jednoznačný návrh (právě 1 kandidát) — předvyplnit. */
  suggested: string | null;
  /**
   * Požadovaní podepisující v pořadí podpisových slotů (1 řádek = 1 osoba).
   * Čárkami oddělení `zastupci` se rozdělí; bez zástupců = jméno vlastníka.
   * SJM („Jan a Marie“) → 2 kandidáti, pokud zástupce = společné jméno / prázdný.
   */
  candidates: string[];
}

/**
 * KN SJM display „Jan Novák a Marie Nováková" → dvě osoby.
 * Jen přesně jeden oddělovač ` a ` (mezery); jinak null (ponechat celek).
 * Neštěpí `a.s.` ani názvy bez mezer kolem `a`.
 *
 * Product rule (SJM → 2 signature pads): OZ §714 — záležitosti SJM, které
 * nelze považovat za běžné, vyžadují společné jednání manželů (nebo jednoho
 * se souhlasem druhého). Souhlas majitele / zatížení nemovitosti (věcné břemeno,
 * služebnost SEK, digitální cesta) je typicky neběžná správa → oba manželé.
 * Plná moc (jiný `zastupci_k_podpisu`) zůstává 1 slot. Není právní posudek.
 */
export function splitSjmJointDisplayName(
  raw: string | null | undefined,
): [string, string] | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  const parts: string[] = [];
  let buf = '';
  let depth = 0;
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && s.slice(i, i + 3) === ' a ') {
      const t = buf.trim();
      if (t) parts.push(t);
      buf = '';
      i += 3;
      continue;
    }
    buf += ch;
    i += 1;
  }
  const t = buf.trim();
  if (t) parts.push(t);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return [parts[0], parts[1]];
}

function isSjmSubjektTyp(subjektTyp: string | null | undefined): boolean {
  return (subjektTyp ?? '').trim().toLowerCase() === 'sjm';
}

function namesMatchCs(a: string, b: string): boolean {
  return a.localeCompare(b, 'cs', { sensitivity: 'accent' }) === 0;
}

/**
 * Default „Zástupci k podpisu“: FO = jméno; SJM = „A, B“ (2 sloty); jinak existující / jméno.
 */
export function defaultZastupciKPodpisu(
  jmenoNazev: string | null | undefined,
  subjektTyp?: string | null,
  existing?: string | null,
): string {
  const existingTrim = (existing ?? '').trim();
  if (existingTrim) return existingTrim;
  const jmeno = (jmenoNazev ?? '').trim();
  if (!jmeno) return '';
  if (isSjmSubjektTyp(subjektTyp)) {
    const spouses = splitSjmJointDisplayName(jmeno);
    if (spouses) return `${spouses[0]}, ${spouses[1]}`;
  }
  return jmeno;
}

/**
 * Rozdělí `zastupci_k_podpisu` („Jméno (funkce), Jméno (funkce)") na jednotlivé zástupce.
 * Čárky uvnitř závorek se ignorují. Parita backend `parseZastupciKPodpisu`.
 */
export function parseZastupciKPodpisu(raw: string | null | undefined): string[] {
  if (raw == null || !String(raw).trim()) return [];
  const parts: string[] = [];
  let buf = '';
  let depth = 0;
  for (const ch of String(raw)) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      const t = buf.trim();
      if (t) parts.push(t);
      buf = '';
      continue;
    }
    buf += ch;
  }
  const t = buf.trim();
  if (t) parts.push(t);
  return parts;
}

/**
 * Podepisující jednoho vlastníka (1 položka = 1 pad).
 * SJM s prázdným / společným zástupcem „A a B“ → 2 osoby (OZ §714 neběžná správa);
 * plná moc (jiné jméno) zůstane 1.
 */
export function resolveOwnerSignatories(o: ContractSignerOwnerRef): string[] {
  const jmeno = (o.jmenoNazev ?? '').trim();
  const parts = parseZastupciKPodpisu(o.zastupciKPodpisu);
  const base = parts.length > 0 ? parts : jmeno ? [jmeno] : [];
  if (!isSjmSubjektTyp(o.subjektTyp) || base.length !== 1) {
    return base.filter(Boolean);
  }
  const only = base[0];
  const isSelfDefault =
    parts.length === 0 || namesMatchCs(only, jmeno);
  if (!isSelfDefault) return [only];
  const spouses =
    splitSjmJointDisplayName(jmeno) ?? splitSjmJointDisplayName(only);
  return spouses ? [...spouses] : [only];
}

/**
 * Seřaděný seznam osob, které musí podepsat (1 položka = 1 podpisový slot / pad).
 * Platí pro **všechny** typy akvizičních smluv (souhlas, digitální cesta, …) —
 * žádný type-specific max-1.
 * Stejná logika jako document lines: per owner rozděl `zastupci`, jinak jméno vlastníka.
 * Duplikáty jmen napříč vlastníky se ponechají (každý slot samostatně).
 * SJM („A a B“) → 2 sloty (OZ §714), pokud zástupce není jiná plná moc — viz
 * `splitSjmJointDisplayName` product-rule komentář.
 */
export function resolveContractSignatories(
  owners: ReadonlyArray<ContractSignerOwnerRef> | null | undefined,
): string[] {
  const out: string[] = [];
  for (const o of owners ?? []) {
    for (const p of resolveOwnerSignatories(o)) {
      if (p) out.push(p);
    }
  }
  return out;
}

/**
 * Návrh jména / fronta podepisujících ze seznamu vlastníků smlouvy.
 * Preferuje rozparsované `zastupciKPodpisu` (1 jméno = 1 kandidát), jinak `jmenoNazev`.
 * Jednoznačné → `suggested` (UI předvyplní / zamkne jméno);
 * více osob → `suggested=null`, UI jde sekvenčně pad po padu přes `candidates`;
 * žádný kandidát → prázdný volný vstup.
 */
export function resolveContractSignerSuggestion(
  owners: ReadonlyArray<ContractSignerOwnerRef> | null | undefined,
): ContractSignerSuggestion {
  const candidates = resolveContractSignatories(owners);
  if (candidates.length === 1) {
    return { suggested: candidates[0], candidates };
  }
  return { suggested: null, candidates };
}
